/**
 * ✅ HELPER: Obtener zona horaria para campañas masivas
 * 
 * Prioridad:
 * 1. Settings de la empresa (key="timezone")
 * 2. localStorage (cache temporal)
 * 3. Fallback: America/Lima (compatible con install.sh)
 * 
 * @param {number} companyId - ID de la empresa
 * @returns {Promise<string>} Zona horaria IANA (ej: "America/Lima")
 */
import { getTimezone } from "../services/settings.api";

const DEFAULT_CAMPAIGN_TIMEZONE = "America/Lima";

export const getCampaignTimezone = async (companyId) => {
  try {
    // Intentar obtener desde Settings (backend)
    // El endpoint /settings/{key} usa el companyId del usuario autenticado
    const response = await getTimezone();
    
    if (response.data && response.data.value) {
      const timezone = response.data.value;
      // Actualizar cache en localStorage
      setCampaignTimezoneCache(timezone);
      return timezone;
    }
  } catch (error) {
    // ✅ CORREGIDO: Solo mostrar warning si NO es un 404 (setting no encontrado es esperado)
    // El 404 es normal cuando la empresa aún no tiene timezone configurado
    if (error.response && error.response.status !== 404) {
      console.warn("[getCampaignTimezone] Error obteniendo timezone desde Settings:", error.message);
    }
    // Si es 404, usar cache silenciosamente (comportamiento esperado)
  }
  
  // Fallback 1: Intentar desde localStorage (cache)
  const cachedTimezone = getCampaignTimezoneSync();
  if (cachedTimezone && cachedTimezone !== DEFAULT_CAMPAIGN_TIMEZONE) {
    return cachedTimezone;
  }
  
  // Fallback 2: Valor por defecto
  return DEFAULT_CAMPAIGN_TIMEZONE;
};

/**
 * ✅ HELPER: Obtener zona horaria sincrónica (desde cache)
 * Útil cuando no se puede hacer llamada async
 * 
 * @returns {string} Zona horaria desde cache o default
 */
export const getCampaignTimezoneSync = () => {
  return localStorage.getItem("campaignTimezone") || DEFAULT_CAMPAIGN_TIMEZONE;
};

/**
 * ✅ HELPER: Actualizar cache de zona horaria
 * 
 * @param {string} timezone - Zona horaria IANA
 */
export const setCampaignTimezoneCache = (timezone) => {
  localStorage.setItem("campaignTimezone", timezone);
};

export default getCampaignTimezone;

